import { AtpAgent, Agent } from '@atproto/api'
import type { LinkedAccount } from '@/pages/Profile/Profile.types'
import { graphqlQuery } from '@/services/graphqlService'
import type {
    GQLUserResponse,
    GQLAPUserResponse,
    ProfileWithBridgeStatus,
    AtpLabel,
} from '@/types/graphql'
import { BLUESKY_PUBLIC_API } from '@/constants/api'
import { fetchAPProfileStats } from '@/lib/mastodon'
import { cleanBridgyFedHandle, isBridgyFedHandle } from '@/lib/bridge'
import { fetchMastodonExtendedBio, splitApHandle } from '@/services/mastodonBioService'
import type { MastodonExtendedBio } from '@/services/mastodonBioService'
import {
    loadBlueskyProfileLinks,
    mergeAtProfileAccounts,
    profileLinksToAccounts,
} from '@/lib/profileLinkAccounts'

/**
 * AT-explore API client for fetching profiles from both AT Protocol and ActivityPub
 */

const AT_EXPLORE_BASE_URL = 'https://atexplore.social'

// Default agent used as fallback when no context-provided agent is available
const DEFAULT_PUBLIC_AGENT = new AtpAgent({ service: BLUESKY_PUBLIC_API })

// Cache of labeler DIDs per user DID — populated once per login session
const labelerDidsCache = new Map<string, string[]>()

/**
 * Configure the agent with the user's subscribed labelers so the AppView returns
 * third-party label data. Results are cached by user DID to avoid re-fetching on
 * every profile view. No-ops for unauthenticated agents.
 */
async function ensureAgentLabelers(agent: Agent): Promise<void> {
    const userDid = agent.did
    if (!userDid) return // Unauthenticated — skip

    if (labelerDidsCache.has(userDid)) {
        agent.configureLabelers(labelerDidsCache.get(userDid)!)
        return
    }

    try {
        // getPreferences() fetches prefs and calls configureLabelers() internally;
        // afterwards agent.labelers reflects the user's subscribed labeler DIDs.
        await agent.getPreferences()
        labelerDidsCache.set(userDid, [...agent.labelers])
    } catch (err) {
        console.warn('[ensureAgentLabelers] Failed to fetch preferences:', err)
        // Fall back gracefully — no third-party labels, but app continues working
    }
}

// Unified profile type matching iOS Profile model
export interface ATExploreProfile {
    did: string
    handle: string
    displayName?: string
    description?: string
    avatar?: string
    banner?: string
    followersCount?: number
    followsCount?: number
    postsCount?: number
    isVerified?: boolean
    pronouns?: string
    bridgyOriginalUrl?: string
    extendedBio?: { heading: string; content: string }
    linkedAccounts?: LinkedAccount[]
    website?: string
    location?: string
    labels?: AtpLabel[]
    /** AT Protocol collections present in the user's repo */
    collections?: string[]
}

// Profile stats response
export interface ProfileStats {
    followersCount: number
    followingCount: number
    postsCount: number
    handle: string
    did: string
}

// AT Protocol profile record response
interface ATProfileRecord {
    uri: string
    cid: string
    value: {
        displayName?: string
        description?: string
        avatar?: { ref: { $link: string } }
        banner?: { ref: { $link: string } }
        bridgyOriginalUrl?: string
    }
}

// DID document response
interface DIDDocumentResponse {
    document: {
        id: string
        alsoKnownAs?: string[]
    }
    verification?: {
        verified: boolean
        verifiers?: string[]
    }
}

// ActivityPub profile response
interface ActivityPubProfile {
    id: string
    type: string
    preferredUsername: string
    name?: string
    summary?: string
    icon?: { url: string } | { url: string }[]
    image?: { url: string } | { url: string }[]
    url?: string | string[]
}

/**
 * Check if a string is a hex-encoded value (e.g. a Nostr pubkey).
 * Hex strings are all lowercase hex characters and typically 64 chars long.
 */
function isHexString(value: string): boolean {
    return /^[0-9a-f]+$/i.test(value) && value.length >= 32
}

/**
 * Check if identifier is an ActivityPub handle (user@domain format)
 */
export function isActivityPubHandle(identifier: string): boolean {
    // Strip leading @ if present (API returns "@user@domain")
    const normalized = identifier.startsWith('@') ? identifier.slice(1) : identifier

    // ActivityPub handles contain @ in the middle (user@domain.com)
    // AT Protocol handles are domain-style (user.bsky.social) or DIDs (did:plc:...)
    const atIndex = normalized.indexOf('@')
    return atIndex > 0 && atIndex < normalized.length - 1
}

/**
 * Get profile for any user (auto-detects protocol)
 */
export async function getProfile(identifier: string): Promise<ATExploreProfile> {
    // Normalize: strip leading @ if present
    const normalized = identifier.startsWith('@') ? identifier.slice(1) : identifier

    if (isActivityPubHandle(normalized)) {
        return getActivityPubProfile(normalized)
    }
    return getATProtocolProfile(normalized)
}

/**
 * Get AT Protocol profile
 */
async function getATProtocolProfile(identifier: string): Promise<ATExploreProfile> {
    const encodedId = encodeURIComponent(identifier)

    // Fetch profile record
    const profileUrl = `${AT_EXPLORE_BASE_URL}/api/explore/at/${encodedId}/app.bsky.actor.profile/self`
    const profileResponse = await fetch(profileUrl)

    if (!profileResponse.ok) {
        throw new Error(`Failed to fetch profile: ${profileResponse.status}`)
    }

    const record: ATProfileRecord = await profileResponse.json()

    // Extract DID from URI (format: at://did:plc:.../...)
    const did = extractDID(record.uri)

    // Get handle and verification status from DID document
    const { handle, isVerified } = await getHandleFromDID(did)

    // Build avatar/banner URLs from CIDs using AT-explore blob endpoint
    const avatarCid = record.value.avatar?.ref.$link
    const bannerCid = record.value.banner?.ref.$link

    return {
        did,
        handle,
        displayName: record.value.displayName,
        description: record.value.description,
        avatar: avatarCid
            ? `${AT_EXPLORE_BASE_URL}/${encodeURIComponent(handle)}/blob/${avatarCid}?preset=avatar`
            : undefined,
        banner: bannerCid
            ? `${AT_EXPLORE_BASE_URL}/${encodeURIComponent(handle)}/blob/${bannerCid}?preset=banner`
            : undefined,
        isVerified,
        bridgyOriginalUrl: record.value.bridgyOriginalUrl,
    }
}

/**
 * Get ActivityPub profile (Mastodon, etc.)
 */
async function getActivityPubProfile(handle: string): Promise<ATExploreProfile> {
    const encodedHandle = encodeURIComponent(handle)
    const url = `${AT_EXPLORE_BASE_URL}/api/explore/ap/${encodedHandle}/profile`

    const response = await fetch(url)
    if (!response.ok) {
        throw new Error(`Failed to fetch ActivityPub profile: ${response.status}`)
    }

    const apProfile: ActivityPubProfile = await response.json()

    // Extract avatar URL (can be object or array)
    const avatarUrl = getFirstUrl(apProfile.icon)

    // Extract banner URL (can be object or array)
    const bannerUrl = getFirstUrl(apProfile.image)

    // Strip HTML from summary
    const description = apProfile.summary ? stripHTML(apProfile.summary) : undefined

    return {
        did: apProfile.id, // Use ActivityPub ID as DID
        handle,
        displayName: apProfile.name,
        description,
        avatar: avatarUrl,
        banner: bannerUrl,
        isVerified: false, // ActivityPub verification works differently
    }
}

/**
 * Get profile stats (AT Protocol only)
 */
export async function getProfileStats(identifier: string): Promise<ProfileStats> {
    if (isActivityPubHandle(identifier)) {
        throw new Error('Stats not available for ActivityPub profiles')
    }

    const encodedId = encodeURIComponent(identifier)
    const url = `${AT_EXPLORE_BASE_URL}/api/explore/at/${encodedId}/stats`

    const response = await fetch(url)
    if (!response.ok) {
        throw new Error(`Failed to fetch profile stats: ${response.status}`)
    }

    return response.json()
}

// --- Helper functions ---

/**
 * Extract DID from AT URI (format: at://did:plc:.../...)
 */
function extractDID(uri: string): string {
    if (uri.startsWith('at://')) {
        const withoutPrefix = uri.slice(5)
        const firstSlash = withoutPrefix.indexOf('/')
        if (firstSlash !== -1) {
            return withoutPrefix.slice(0, firstSlash)
        }
        return withoutPrefix
    }
    return uri
}

/**
 * Get handle and verification status from DID document
 */
async function getHandleFromDID(did: string): Promise<{ handle: string; isVerified: boolean }> {
    const url = `${AT_EXPLORE_BASE_URL}/api/explore/at/${encodeURIComponent(did)}`

    const response = await fetch(url)
    if (!response.ok) {
        return { handle: did, isVerified: false }
    }

    const data: DIDDocumentResponse = await response.json()

    // Extract handle from alsoKnownAs (format: "at://handle")
    let handle = did
    const alsoKnownAs = data.document.alsoKnownAs?.[0]
    if (alsoKnownAs?.startsWith('at://')) {
        handle = alsoKnownAs.slice(5)
    }

    const isVerified = data.verification?.verified ?? false

    return { handle, isVerified }
}

/**
 * Get first URL from ActivityPub image field (can be object or array)
 */
function getFirstUrl(field: { url: string } | { url: string }[] | undefined): string | undefined {
    if (!field) return undefined
    if (Array.isArray(field)) {
        return field[0]?.url
    }
    return field.url
}

/**
 * Strip HTML tags from text
 */
function stripHTML(html: string): string {
    let result = html

    // Remove HTML tags
    result = result.replace(/<[^>]+>/g, '')

    // Decode common HTML entities
    result = result
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')

    return result.trim()
}

/**
 * Fetch verification, stats, and labels from the Bluesky app view's getProfile endpoint.
 */
async function fetchBlueskyNativeProfile(
    actor: string,
    agent: Agent
): Promise<{
    ok: boolean
    did?: string
    isVerified: boolean
    handle?: string
    followersCount?: number
    followsCount?: number
    postsCount?: number
    labels?: AtpLabel[]
    displayName?: string
    description?: string
    avatar?: string
    banner?: string
    followedBy?: string
    /** Raw getProfile payload, so `links` survives an SDK lexicon that predates it. */
    data?: Record<string, unknown>
}> {
    try {
        const res = await agent.app.bsky.actor.getProfile({ actor })
        const data = res.data as unknown as Record<string, unknown>
        // Extract and normalize labels from the SDK response
        const labels: AtpLabel[] = (res.data.labels ?? []).map(l => ({
            src: l.src,
            uri: l.uri,
            val: l.val,
            neg: l.neg,
            cts: l.cts,
            exp: l.exp,
        }))
        return {
            ok: true,
            did: res.data.did,
            isVerified: res.data.verification?.verifiedStatus === 'valid',
            handle: res.data.handle,
            followersCount: res.data.followersCount,
            followsCount: res.data.followsCount,
            postsCount: res.data.postsCount,
            labels: labels.length > 0 ? labels : undefined,
            displayName: res.data.displayName,
            description: res.data.description,
            avatar: res.data.avatar,
            banner: res.data.banner,
            followedBy: res.data.viewer?.followedBy,
            data,
        }
    } catch (err) {
        console.warn('[fetchBlueskyNativeProfile] Failed:', err)
        return { ok: false, isVerified: false }
    }
}

// --- GraphQL Queries ---

const AT_PROFILE_QUERY = `
query GetUser($identifier: String!) {
  user(identifier: $identifier) {
    did
    repo { handle handleIsCorrect collections }
    avatar { cdn pds }
    banner { cdn pds }
    backlinkSources { collection path count }
    bridgeStatus { isBridged sourceProtocol activityPubHandle activityPubUrl bridgyFedUrl nostrPubkey }
    record(collection: "app.bsky.actor.profile", rkey: "self") {
      uri
      cid
      value
    }
    mackinacProfile: record(collection: "social.mackinac.actor.profile", rkey: "self") {
      uri
      cid
      value
    }
  }
}
`

const AP_PROFILE_QUERY = `
query GetAPUser($handle: String!) {
  apUser(handle: $handle) {
    handle
    displayName
    description
    avatarUrl
    bannerUrl
    actorUrl
    stats { followersCount followingCount postsCount }
    bridgeStatus { isBridged blueskyHandle blueskyDid }
  }
}
`

const AT_MACKINAC_PROFILE_QUERY = `
query GetMackinacProfile($identifier: String!) {
  user(identifier: $identifier) {
    mackinacProfile: record(collection: "social.mackinac.actor.profile", rkey: "self") { value }
  }
}
`

export interface ParsedMackinacProfile {
    extendedBio?: { heading: string; content: string }
    linkedAccounts?: { rkey: string; icon: string; name: string; link: string; order: number }[]
    website?: string
    location?: string
}

export function parseMackinacProfileValue(
    value: Record<string, unknown> | undefined
): ParsedMackinacProfile {
    if (!value) return {}
    const headline = extractString(value.headline)
    const description = extractString(value.description)
    const rawAccounts = value.links
    const linkedAccounts = Array.isArray(rawAccounts)
        ? rawAccounts
              .filter((entry): entry is Record<string, unknown> =>
                  Boolean(entry && typeof entry === 'object')
              )
              .map((entry, index) => ({
                  rkey: `embedded-${index}`,
                  icon: extractString(entry.icon) ?? '',
                  name: extractString(entry.name) ?? '',
                  link: extractString(entry.link) ?? '',
                  order: typeof entry.order === 'number' ? entry.order : 999,
              }))
              .sort((a, b) => a.order - b.order)
        : undefined
    return {
        extendedBio:
            headline && description ? { heading: headline, content: description } : undefined,
        linkedAccounts,
        website: extractString(value.website),
        location: extractString(value.location),
    }
}

async function fetchMackinacProfileForBridged(
    identifier: string
): Promise<ParsedMackinacProfile | undefined> {
    const response = await graphqlQuery<{
        user?: { mackinacProfile?: { value?: Record<string, unknown> } }
    }>(AT_MACKINAC_PROFILE_QUERY, { identifier })
    if (response.errors?.length) return undefined
    const value = response.data?.user?.mackinacProfile?.value
    return value ? parseMackinacProfileValue(value) : undefined
}

/**
 * Get profile via GraphQL (supports both AT Protocol and ActivityPub)
 * Returns profile with stats and bridge status in a single query
 */
export async function getProfileGraphQL(
    identifier: string,
    options?: { publicAgent?: Agent }
): Promise<ProfileWithBridgeStatus> {
    // Normalize: strip leading @ if present
    const normalized = identifier.startsWith('@') ? identifier.slice(1) : identifier

    if (isActivityPubHandle(normalized)) {
        return getAPProfileGraphQL(normalized, options)
    }
    return getATProfileGraphQL(normalized, options)
}

/**
 * Safely extract a string value from an unknown type
 */
function extractString(value: unknown): string | undefined {
    return typeof value === 'string' ? value : undefined
}

/**
 * Get AT Protocol profile via GraphQL
 */
export async function getATProfileGraphQL(
    identifier: string,
    options?: { publicAgent?: Agent }
): Promise<ProfileWithBridgeStatus> {
    const agent = options?.publicAgent ?? DEFAULT_PUBLIC_AGENT

    // Only call the Bluesky AppView getProfile endpoint for DID or handle identifiers.
    // Non-DID identifiers (e.g. Nostr hex pubkeys) are not valid Bluesky actors and will
    // return 400 errors from the PDS. For those, skip the native fetch entirely.
    const isATProtocolIdentifier = identifier.startsWith('did:') || !isHexString(identifier)

    // Configure labeler subscriptions so the AppView returns third-party label data.
    // This is a no-op for unauthenticated agents.
    await ensureAgentLabelers(agent)

    // Fetch GraphQL profile and native Bluesky stats in parallel.
    // AppView (native) is the primary source for metrics; GraphQL is the fallback.
    const [responseResult, nativeStats] = await Promise.all([
        graphqlQuery<GQLUserResponse>(AT_PROFILE_QUERY, { identifier }).catch(err => {
            console.warn('[getATProfileGraphQL] GraphQL failed:', err)
            return null
        }),
        isATProtocolIdentifier
            ? fetchBlueskyNativeProfile(identifier, agent)
            : Promise.resolve({
                  ok: false as const,
                  did: undefined,
                  isVerified: false,
                  handle: undefined,
                  followersCount: undefined,
                  followsCount: undefined,
                  postsCount: undefined,
                  labels: undefined,
                  displayName: undefined,
                  description: undefined,
                  avatar: undefined,
                  banner: undefined,
                  followedBy: undefined,
                  data: undefined,
              }),
    ])

    // If GraphQL failed entirely (null) or returned errors with no usable data, fall back to
    // the native-only minimal profile. For partial GraphQL errors (errors + data.user present),
    // fall through and use the data we have.
    const user = responseResult?.data?.user
    if (!responseResult || (!user && responseResult.errors?.length)) {
        const did = nativeStats.did ?? (identifier.startsWith('did:') ? identifier : '')
        const bluesky = await loadBlueskyProfileLinks({
            did,
            actor: identifier,
            getProfileOk: nativeStats.ok,
            data: nativeStats.data,
            profileRecord: undefined,
            agent,
        })
        return {
            did: nativeStats.did ?? identifier,
            // The AppView already returned the real handle in parallel; only fall back
            // to the identifier (which may be a DID) when no source has one.
            handle: nativeStats.handle ?? identifier,
            displayName: nativeStats.displayName,
            description: nativeStats.description,
            avatar: nativeStats.avatar,
            banner: nativeStats.banner,
            followersCount: nativeStats.followersCount,
            followsCount: nativeStats.followsCount,
            postsCount: nativeStats.postsCount,
            isVerified: nativeStats.isVerified,
            labels: nativeStats.labels,
            followedBy: nativeStats.followedBy,
            linkedAccounts: mergeAtProfileAccounts({ bluesky }),
        }
    }

    if (!user) {
        throw new Error('Profile not found')
    }

    // Extract profile values from record.value (JSON scalar)
    const recordValue = user.record?.value as Record<string, unknown> | undefined

    const mackinacValue = user.mackinacProfile?.value as Record<string, unknown> | undefined
    const {
        extendedBio,
        linkedAccounts: mackinacAccounts,
        website,
        location,
    } = parseMackinacProfileValue(mackinacValue)
    const profileDid = user.did.startsWith('did:') ? user.did : (nativeStats.did ?? '')
    const blueskyLinks = await loadBlueskyProfileLinks({
        did: profileDid,
        actor: identifier,
        getProfileOk: nativeStats.ok,
        data: nativeStats.data,
        profileRecord: recordValue,
        agent,
    })
    const linkedAccounts = mergeAtProfileAccounts({
        bluesky: blueskyLinks,
        mackinac: mackinacAccounts,
    })
    const mackinacLinks = mackinacAccounts?.map(account => ({
        ...account,
        source: 'mackinac' as const,
    }))
    const storedBlueskyLinks = profileLinksToAccounts(blueskyLinks)

    // Derive followers count from backlinkSources (GraphQL) as fallback
    let gqlFollowersCount: number | undefined
    if (user.backlinkSources) {
        const followBacklinks = user.backlinkSources.find(
            s => s.collection === 'app.bsky.graph.follow' && s.path === 'subject'
        )
        if (followBacklinks) {
            gqlFollowersCount = followBacklinks.count
        }
    }

    // Prefer AppView (native) data — it's fresh from the PDS and not subject to AT-explore's
    // server-side cache. Fall back to AT-explore GraphQL values only when native fetch failed.
    // GraphQL only has raw backlink counts for followers (no followsCount or postsCount).
    return {
        did: user.did,
        handle: user.repo?.handle ?? '',
        displayName: nativeStats.displayName ?? extractString(recordValue?.displayName),
        description: nativeStats.description ?? extractString(recordValue?.description),
        avatar: nativeStats.avatar ?? user.avatar?.cdn ?? user.avatar?.pds,
        banner: nativeStats.banner ?? user.banner?.cdn ?? user.banner?.pds,
        followersCount: nativeStats.followersCount ?? gqlFollowersCount,
        followsCount: nativeStats.followsCount,
        postsCount: nativeStats.postsCount,
        isVerified: nativeStats.isVerified,
        pronouns: extractString(recordValue?.pronouns),
        bridgyOriginalUrl: extractString(recordValue?.bridgyOriginalUrl),
        bridgeStatus: user.bridgeStatus
            ? {
                  isBridged: user.bridgeStatus.isBridged,
                  sourceProtocol: user.bridgeStatus.sourceProtocol,
                  activityPubHandle: user.bridgeStatus.activityPubHandle,
                  activityPubUrl: user.bridgeStatus.activityPubUrl,
                  bridgyFedUrl: user.bridgeStatus.bridgyFedUrl,
                  nostrPubkey: user.bridgeStatus.nostrPubkey,
              }
            : undefined,
        extendedBio,
        linkedAccounts,
        mackinacLinks,
        ...(storedBlueskyLinks.length > 0 ? { blueskyLinks: storedBlueskyLinks } : {}),
        website,
        location,
        labels: nativeStats.labels,
        followedBy: nativeStats.followedBy,
        collections: user.repo?.collections,
    }
}

/**
 * Get ActivityPub profile via GraphQL
 */
export async function getAPProfileGraphQL(
    handle: string,
    options?: { publicAgent?: Agent; includeMackinacBio?: boolean }
): Promise<ProfileWithBridgeStatus> {
    // Fetch GraphQL profile (includes stats) and native Mastodon stats in parallel
    const [response, nativeStats] = await Promise.all([
        graphqlQuery<GQLAPUserResponse>(AP_PROFILE_QUERY, { handle }),
        fetchAPProfileStats(handle).catch(err => {
            console.warn('[getAPProfileGraphQL] Failed to fetch native stats:', err)
            return undefined
        }),
    ])

    if (response.errors?.length) {
        throw new Error(response.errors[0].message)
    }

    const apUser = response.data?.apUser
    if (!apUser) {
        throw new Error('Profile not found')
    }

    // For bridged profiles, check AT Protocol verification status using the blueskyDid
    const blueskyDid = apUser.bridgeStatus?.blueskyDid
    const blueskyAgent = options?.publicAgent ?? DEFAULT_PUBLIC_AGENT

    // Kick off mackinac bio fetch in parallel with bluesky native profile fetch
    const [blueskyNative, mackinacBio] = await Promise.all([
        blueskyDid
            ? fetchBlueskyNativeProfile(blueskyDid, blueskyAgent)
            : Promise.resolve(undefined),
        (async () => {
            if (options?.includeMackinacBio === false) return undefined
            if (isBridgyFedHandle(handle)) {
                const identifier = blueskyDid ?? cleanBridgyFedHandle(handle)
                return identifier
                    ? await fetchMackinacProfileForBridged(identifier).catch(() => undefined)
                    : undefined
            }
            try {
                const { instance, acct } = splitApHandle(handle)
                return await fetchMastodonExtendedBio(instance, acct).catch(() => null)
            } catch {
                return null
            }
        })(),
    ])

    const extendedBio = mackinacBio
        ? 'headline' in mackinacBio
            ? mackinacBio.headline && mackinacBio.description
                ? { heading: mackinacBio.headline, content: mackinacBio.description }
                : undefined
            : mackinacBio.extendedBio
        : undefined

    // Prefer native Mastodon stats, fall back to GraphQL stats
    // Pronouns come from extended bio when available (non-bridged path
    // via MastodonExtendedBio), falling back to native Mastodon fields
    // for backwards compatibility.
    const pronounsFromBio =
        mackinacBio && 'pronouns' in mackinacBio
            ? ((mackinacBio as MastodonExtendedBio).pronouns ?? undefined)
            : undefined
    const pronounsFromFields = nativeStats?.rawFields
        ? extractPronounsFromFields(nativeStats.rawFields)
        : undefined
    return {
        did: apUser.actorUrl ?? handle, // Use actorUrl as DID for AP profiles
        handle: apUser.handle,
        displayName: apUser.displayName,
        description: apUser.description,
        avatar: apUser.avatarUrl,
        banner: apUser.bannerUrl,
        followersCount: nativeStats?.followersCount ?? apUser.stats?.followersCount,
        // Note: native API uses followsCount, GraphQL schema uses followingCount
        followsCount: nativeStats?.followsCount ?? apUser.stats?.followingCount,
        postsCount: nativeStats?.postsCount ?? apUser.stats?.postsCount,
        isVerified: blueskyNative?.isVerified ?? false,
        linkedAccounts: nativeStats?.linkedAccounts,
        rawMastodonFields: nativeStats?.rawFields,
        extendedBio,
        pronouns: pronounsFromBio ?? pronounsFromFields,
        website: mackinacBio?.website ?? undefined,
        location: mackinacBio?.location ?? undefined,
        bridgeStatus: apUser.bridgeStatus
            ? {
                  isBridged: apUser.bridgeStatus.isBridged,
                  blueskyHandle: apUser.bridgeStatus.blueskyHandle,
                  blueskyDid: apUser.bridgeStatus.blueskyDid,
              }
            : undefined,
    }
}

function extractPronounsFromFields(fields: { name: string; value: string }[]): string | undefined {
    const field = fields.find(f => f.name.toLowerCase() === 'pronouns')
    return field ? field.value : undefined
}
